/**
 * Habit Store - Zustand store for habit tracking state management.
 *
 * Manages habits, today's completions, streak data, and integrates with
 * the event bus for cross-module streak milestone notifications.
 *
 * @see Requirements 8.1, 8.2, 8.3, 8.9, 10.4
 */

import { create } from 'zustand';
import type { Habit, HeatmapEntry } from '../types';
import { habitService } from '../services/habitService';
import { eventBus } from '../services/eventBus';

// ─── Constants ───────────────────────────────────────────────────────────────

/** Streak milestones that trigger the 'habit:streak-milestone' event. */
const STREAK_MILESTONES = [7, 14, 30, 60, 90];

// ─── Helper Functions ────────────────────────────────────────────────────────

/**
 * Get today's date in YYYY-MM-DD format.
 */
function getTodayDate(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// ─── Types ───────────────────────────────────────────────────────────────────

export interface HabitState {
  /** All active (non-archived) habits. */
  habits: Habit[];
  /** Array of habitIds completed today. Used as Set-like lookup. */
  todayCompletions: string[];
  /** Currently selected habit ID for detail view. */
  selectedHabitId: string | null;
  /** Whether a habit operation is in progress. */
  isLoading: boolean;
  /** Last error message from a habit operation. */
  error: string | null;
}

export interface HabitActions {
  /** Load habits from local DB. Pass true to include archived habits. */
  loadHabits(includeArchived?: boolean): Promise<void>;
  /** Create a new habit. */
  createHabit(input: {
    name: string;
    description?: string;
    targetTime?: string;
    color?: string;
  }): Promise<Habit>;
  /** Update a habit's fields. */
  updateHabit(habitId: string, updates: Partial<Habit>): Promise<void>;
  /** Archive a habit (remove from active list, preserve history). */
  archiveHabit(habitId: string): Promise<void>;
  /** Toggle today's completion for a habit. If completed, remove; otherwise record. */
  toggleCompletion(habitId: string): Promise<void>;
  /**
   * Toggle completion for a habit on an explicit date (YYYY-MM-DD).
   *
   * Checks whether a completion already exists for (habitId, date). If it does,
   * the completion is removed; otherwise it is recorded. Supports backfilling
   * past days. After the change the habit's streak is recomputed and mirrored
   * into local state, `todayCompletions` is kept in sync when `date` is today,
   * and a streak-milestone event is emitted when a milestone is reached.
   */
  toggleCompletionForDate(habitId: string, date: string): Promise<void>;
  /** Load today's completion status for all habits. */
  loadTodayCompletions(): Promise<void>;
  /** Get streak data for a habit. */
  getStreak(habitId: string): Promise<{ currentStreak: number; longestStreak: number }>;
  /** Get heatmap data for a habit over a date range. */
  getHeatmap(habitId: string, startDate: string, endDate: string): Promise<HeatmapEntry[]>;
  /** Set the currently selected habit. */
  selectHabit(habitId: string | null): void;
}

export type HabitStore = HabitState & HabitActions;

// ─── Store ───────────────────────────────────────────────────────────────────

export const useHabitStore = create<HabitStore>((set, get) => ({
  // ── State ──────────────────────────────────────────────────────────────────
  habits: [],
  todayCompletions: [],
  selectedHabitId: null,
  isLoading: false,
  error: null,

  // ── Actions ────────────────────────────────────────────────────────────────

  loadHabits: async (includeArchived = false) => {
    set({ isLoading: true, error: null });
    try {
      const habits = await habitService.getHabits(includeArchived);
      set({ habits, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load habits';
      set({ isLoading: false, error: message });
    }
  },

  createHabit: async (input) => {
    set({ error: null });
    try {
      const habit = await habitService.createHabit(input);
      set((state) => ({ habits: [...state.habits, habit] }));
      return habit;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create habit';
      set({ error: message });
      throw error;
    }
  },

  updateHabit: async (habitId, updates) => {
    set({ error: null });
    try {
      const updatedHabit = await habitService.updateHabit(habitId, updates);
      set((state) => ({
        habits: state.habits.map((h) => (h.habitId === habitId ? updatedHabit : h)),
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update habit';
      set({ error: message });
      throw error;
    }
  },

  archiveHabit: async (habitId) => {
    set({ error: null });
    try {
      await habitService.archiveHabit(habitId);
      set((state) => ({
        habits: state.habits.filter((h) => h.habitId !== habitId),
        todayCompletions: state.todayCompletions.filter((id) => id !== habitId),
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to archive habit';
      set({ error: message });
      throw error;
    }
  },

  toggleCompletion: async (habitId) => {
    set({ error: null });
    const { todayCompletions } = get();
    const isCompleted = todayCompletions.includes(habitId);

    try {
      if (isCompleted) {
        // Remove completion
        await habitService.removeCompletion(habitId);
        set((state) => ({
          todayCompletions: state.todayCompletions.filter((id) => id !== habitId),
        }));
      } else {
        // Record completion
        await habitService.recordCompletion(habitId);
        set((state) => ({
          todayCompletions: [...state.todayCompletions, habitId],
        }));

        // Check for streak milestones after recording
        const streakData = await habitService.calculateStreak(habitId);
        if (STREAK_MILESTONES.includes(streakData.currentStreak)) {
          eventBus.emit('habit:streak-milestone', {
            habitId,
            streakDays: streakData.currentStreak,
          });
        }

        // Update the habit's streak in local state
        set((state) => ({
          habits: state.habits.map((h) =>
            h.habitId === habitId
              ? {
                  ...h,
                  currentStreak: streakData.currentStreak,
                  longestStreak: streakData.longestStreak,
                }
              : h
          ),
        }));
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to toggle completion';
      set({ error: message });
      throw error;
    }
  },

  toggleCompletionForDate: async (habitId, date) => {
    set({ error: null });

    try {
      // Determine current completion state for the given date.
      const existing = await habitService.getCompletions(habitId, date, date);
      const isCompleted = existing.length > 0;

      if (isCompleted) {
        await habitService.removeCompletion(habitId, date);
      } else {
        await habitService.recordCompletion(habitId, date);
      }

      // Recompute streak after the change.
      const streakData = await habitService.calculateStreak(habitId);

      // Emit milestone event only when a new completion hits a milestone.
      if (!isCompleted && STREAK_MILESTONES.includes(streakData.currentStreak)) {
        eventBus.emit('habit:streak-milestone', {
          habitId,
          streakDays: streakData.currentStreak,
        });
      }

      const today = getTodayDate();
      const isToday = date === today;

      set((state) => ({
        habits: state.habits.map((h) =>
          h.habitId === habitId
            ? {
                ...h,
                currentStreak: streakData.currentStreak,
                longestStreak: streakData.longestStreak,
              }
            : h
        ),
        todayCompletions: isToday
          ? isCompleted
            ? state.todayCompletions.filter((id) => id !== habitId)
            : state.todayCompletions.includes(habitId)
              ? state.todayCompletions
              : [...state.todayCompletions, habitId]
          : state.todayCompletions,
      }));
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to toggle completion';
      set({ error: message });
      throw error;
    }
  },

  loadTodayCompletions: async () => {
    set({ error: null });
    try {
      const today = getTodayDate();
      // Get all habits to check completions for each
      const habits = get().habits;
      const completedIds: string[] = [];

      for (const habit of habits) {
        const completions = await habitService.getCompletions(habit.habitId, today, today);
        if (completions.length > 0) {
          completedIds.push(habit.habitId);
        }
      }

      set({ todayCompletions: completedIds });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to load today completions';
      set({ error: message });
    }
  },

  getStreak: async (habitId) => {
    try {
      return await habitService.calculateStreak(habitId);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to get streak';
      set({ error: message });
      return { currentStreak: 0, longestStreak: 0 };
    }
  },

  getHeatmap: async (habitId, startDate, endDate) => {
    try {
      return await habitService.getHeatmapData(habitId, startDate, endDate);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to get heatmap';
      set({ error: message });
      return [];
    }
  },

  selectHabit: (habitId) => {
    set({ selectedHabitId: habitId });
  },
}));
